/**
 * MetricsBar Component
 * Displays KPI metrics (cost, tokens, hit rate, latency)
 * Redesigned for clarity and non-technical accessibility
 */

import React, { useState, useEffect } from 'react';
import { DollarSign, Zap, Target, Gauge, ShieldCheck } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';

interface KPICardProps {
  title: string;
  subtitle: string;
  value: string | number;
  icon: React.ReactNode;
  unit?: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
  loading?: boolean;
  tooltip?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  subtitle,
  value,
  icon,
  unit,
  color,
  loading,
  tooltip,
}) => {
  const gradientClasses = {
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 hover:border-blue-400 hover:shadow-[0_0_15px_rgba(59,130,246,0.3)]',
    green: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 hover:border-emerald-400 hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]',
    purple: 'from-violet-500/20 to-violet-600/5 border-violet-500/30 hover:border-violet-400 hover:shadow-[0_0_15px_rgba(139,92,246,0.3)]',
    orange: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 hover:border-amber-400 hover:shadow-[0_0_15px_rgba(245,158,11,0.3)]',
  };

  const iconBgClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    purple: 'from-violet-500 to-violet-600',
    orange: 'from-amber-500 to-amber-600',
  };

  const valueColorClasses = {
    blue: 'text-blue-400',
    green: 'text-emerald-400',
    purple: 'text-violet-400',
    orange: 'text-amber-400',
  };

  return (
    <div
      className={`bg-slate-900/40 ${gradientClasses[color]} border rounded-2xl p-4 backdrop-blur-md transition-all duration-300 group relative overflow-hidden`}
      title={tooltip}
    >
      <div className="flex items-center justify-between relative gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest truncate">{title}</p>

          {loading ? (
            <div className="mt-1.5 space-y-1.5">
              <div className="h-6 w-16 bg-white/5 rounded animate-pulse" />
            </div>
          ) : (
            <>
              <p className={`text-2xl font-black ${valueColorClasses[color]} mt-1 leading-none tracking-tight`}>
                {value}
                {unit && <span className="text-xs font-semibold text-slate-500 ml-1">{unit}</span>}
              </p>
              <p className="text-[10px] text-slate-500 mt-1 leading-tight truncate uppercase font-medium">{subtitle}</p>
            </>
          )}
        </div>

        <div className={`bg-gradient-to-br ${iconBgClasses[color]} p-2 rounded-lg shadow-lg flex-shrink-0 opacity-80 group-hover:opacity-100 transition-opacity`}>
          <div className="text-white">{icon}</div>
        </div>
      </div>
    </div>
  );
};

export const MetricsBar: React.FC = () => {
  const metrics = useDashboardStore((state) => state.metrics);
  const isLoading = useDashboardStore((state) => state.isLoadingMetrics);
  const [secondsAgo, setSecondsAgo] = useState(0);

  useEffect(() => {
    setSecondsAgo(0);
    const timer = setInterval(() => setSecondsAgo((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [metrics]);

  if (!metrics) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <KPICard
            key={i}
            title="Syncing..."
            subtitle="Secure bridge active"
            value="-"
            icon={<Zap className="w-4 h-4" />}
            color="blue"
            loading={true}
          />
        ))}
      </div>
    );
  }

  const hitRatePct = metrics.hitRate * 100;
  const isShieldSecure = !metrics.secretsMasked || metrics.secretsMasked === 0;
  const tokensFormatted = metrics.tokensSaved >= 1000 ? `${(metrics.tokensSaved / 1000).toFixed(1)}k` : metrics.tokensSaved.toLocaleString();
  const speedupFactor = metrics.averageLatency > 0 ? (2500 / metrics.averageLatency).toFixed(0) : '80';

  return (
    <>
      <div className="grid grid-cols-4 gap-4">
        <KPICard
          title="Privacy Shield"
          subtitle={isShieldSecure ? "Zero leaks detected" : "Secrets masked locally"}
          value={isShieldSecure ? "SECURE" : (metrics.secretsMasked ?? 0)}
          icon={<ShieldCheck className="w-4 h-4" />}
          color={isShieldSecure ? "green" : "blue"}
          loading={isLoading}
          tooltip="Lemma Privacy Firewall intercepts PII and secrets before they reach the cloud."
        />
        <KPICard
          title="Budget Saved"
          subtitle={`Total Saved (All-Time) ~${tokensFormatted}`}
          value={metrics.costSaved < 1 ? `$${metrics.costSaved.toFixed(4)}` : `$${metrics.costSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={<DollarSign className="w-4 h-4" />}
          color="green"
          loading={isLoading}
          tooltip="Every time a cached response is reused, the system avoids paying the AI provider."
        />
        <KPICard
          title="Global Hit Rate"
          subtitle="Semantic memory efficiency"
          value={`${hitRatePct.toFixed(1)}`}
          unit="%"
          icon={<Target className="w-4 h-4" />}
          color="orange"
          loading={isLoading}
          tooltip="When an AI agent asks the same question again, the system returns the cached answer instantly."
        />
        <KPICard
          title="Response Time"
          subtitle={`vs. 2.5s direct LLM (${speedupFactor}x speed)`}
          value={metrics.averageLatency.toFixed(0)}
          unit="ms"
          icon={<Gauge className="w-4 h-4" />}
          color="blue"
          loading={isLoading}
          tooltip="How quickly the system responds. Lower is faster. Cached hits respond in <10ms."
        />
      </div>
      <p className="text-right text-xs text-gray-400 mt-1 pr-0.5">
        {isLoading ? 'Updating…' : `Updated ${secondsAgo === 0 ? 'just now' : `${secondsAgo}s ago`}`}
      </p>
    </>
  );
};

